import { CvssTable } from '../../../../../cvssTable';
import { Link, Shimmer } from '@fluentui/react';
import { useTranslation, Trans } from 'react-i18next';
import { HelpText } from '@msrceng/component';
import { useVulnerabilitySingleApi } from '../../../../../../api/vulnerability';
import { getScoreText } from './getScoreText';
import { cvssVersion } from './cvssVersion';

/** URL to the website detailing what a CVSS score is. */
const cvssDefinitionUrl = 'https://www.first.org/cvss';

/** Component that renders the "Security Updates" list
 */

export const CvssView = ({ cveNumber }: { cveNumber: string }) => {
  const { data, isLoading } = useVulnerabilitySingleApi(cveNumber);
  const [t] = useTranslation(['cvss', 'vulnerability']);
  if (!isLoading && !data?.vectorString) {
    return null;
  }
  const version = cvssVersion(data);
  const scoreText = getScoreText(data);
  const { baseScore, temporalScore, vectorString } = data || {};
  return (
    <>
      <CvssTable loading={isLoading} vectorString={data?.vectorString}>
        {vectorString && (
          <dl>
            <dt>
              {t('Vector String', {
                defaultValue: 'Vector string',
                ns: 'search',
              })}
            </dt>
            <dd>
              <code
                style={{
                  // Wrap the vector string because it's long and we don't want it to overflow.
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {vectorString}
              </code>
            </dd>
          </dl>
        )}
        <Shimmer
          width='6em'
          styles={{ root: { paddingBottom: '0.5em' } }}
          isDataLoaded={!isLoading}
        >
          <dl className='print-cvss-label'>
            <dt>
              {t('Metrics', {
                defaultValue: 'Metrics',
                ns: 'cvss',
              })}
            </dt>
            <dd>
              {version} {scoreText}
              <HelpText
                ariaLabel={
                  version && scoreText
                    ? `${version} ${scoreText} help text expanded`
                    : undefined
                }
                ariaDescription={
                  baseScore && temporalScore
                    ? `Base score metrics: ${baseScore} / Temporal score metrics: ${temporalScore}`
                    : undefined
                }
              >
                {t('score description', {
                  baseScore,
                  temporalScore,
                  ns: 'cvss',
                  defaultValue:
                    'Base score metrics: {{baseScore}} / Temporal score metrics: {{temporalScore}}',
                })}
              </HelpText>
            </dd>
          </dl>
        </Shimmer>
      </CvssTable>
      <p>
        <Trans
          t={t}
          i18nKey={'vulnerability:cvss:text'}
          defaults='Please see <0>Common Vulnerability Scoring System</0> for more information on the definition of these metrics.'
          components={[
            <Link
              key='linkKey'
              href={cvssDefinitionUrl}
              target='_blank'
              rel='noopener noreferrer'
            />,
          ]}
        />
      </p>
    </>
  );
};
